<template>
  <div class="sp-view-toggle" role="radiogroup" :aria-label="groupLabel">
    <label
      v-for="option in options"
      :key="option.value"
      class="sp-view-toggle__option"
      :title="option.title"
      :data-testid="option.data.optionTestId"
    >
      <input
        class="sp-view-toggle__input"
        type="radio"
        :name="inputName"
        :value="option.value"
        :checked="selectedValue === option.value"
        :aria-label="option.label"
        @input.stop
        @change="handleChange"
      />
      <span class="sp-view-toggle__box">
        <sp-icon v-if="option.data.icon" class="sp-view-toggle__icon" :name="option.data.icon"></sp-icon>
        <span class="sp-view-toggle__text">{{ option.label }}</span>
      </span>
    </label>
  </div>
</template>

<script setup>
const props = defineProps({
  /**
   * Accessible name of the radio group.
   *
   * @type {String}
   */
  label: {
    type: String,
    default: "",
  },
});

import { useViewToggleControl } from "./composables/useViewToggleControl";
const { groupLabel, options, inputName, selectedValue, handleChange } = useViewToggleControl(props);
</script>

<style lang="scss">
:host {
  display: inline-flex;
}

.sp-view-toggle {
  display: flex;

  &__option {
    cursor: pointer;
    display: flex;

    &:hover {
      background-color: var(--sp-comp-view-toggle-hover-background-color, var(--sp-sys-color-primary-container));
    }
  }

  &__input {
    height: 0;
    opacity: 0;
    position: absolute;
    width: 0;
  }

  &__box {
    align-items: var(--sp-comp-view-toggle-align-items, center);
    border: 1px solid var(--sp-comp-view-toggle-border-color, var(--sp-sys-color-outline-variant));
    color: var(--sp-comp-view-toggle-text-color, var(--sp-sys-color-primary));
    display: flex;
    gap: var(--sp-comp-view-toggle-gap, 0);
    font-family: var(--sp-comp-view-toggle-font-family, inherit);
    font-size: var(--sp-comp-view-toggle-font-size, inherit);
    font-weight: var(--sp-comp-view-toggle-font-weight, inherit);
    padding: var(--sp-comp-view-toggle-padding, var(--sp-ref-scale-6));
  }

  &__option:first-child &__box {
    border-radius: var(--sp-comp-view-toggle-border-radius, var(--sp-sys-shape-control)) 0 0
      var(--sp-comp-view-toggle-border-radius, var(--sp-sys-shape-control));
    border-right-width: 0;
  }

  &__option:last-child &__box {
    border-left-width: 0;
    border-radius: 0 var(--sp-comp-view-toggle-border-radius, var(--sp-sys-shape-control))
      var(--sp-comp-view-toggle-border-radius, var(--sp-sys-shape-control)) 0;
  }

  &__input:checked + &__box {
    background-color: var(--sp-comp-view-toggle-active-background-color, var(--sp-sys-color-primary));
    color: var(--sp-comp-view-toggle-active-text-color, var(--sp-sys-color-on-primary));
    text-decoration: var(--sp-comp-view-toggle-active-text-decoration, none);
    text-underline-offset: var(--sp-comp-view-toggle-active-text-underline-offset, auto);
    text-decoration-thickness: var(--sp-comp-view-toggle-active-text-decoration-thickness, auto);
  }

  &__input:focus-visible + &__box {
    outline: var(--sp-sys-state-focus-indicator-thickness) solid
      var(--sp-comp-view-toggle-text-color, var(--sp-sys-state-focus-indicator-color));
    outline-offset: var(--sp-sys-state-focus-indicator-outer-offset);
  }

  &__icon {
    --sp-comp-icon-fill: currentcolor;
    --sp-comp-icon-size: var(--sp-comp-view-toggle-icon-size, var(--sp-sys-icon-size-small));
  }

  &__text {
    display: var(--sp-comp-view-toggle-text-display, none);
    line-height: var(--sp-comp-view-toggle-icon-size, var(--sp-sys-icon-size-small));
  }
}
</style>
